import { useState, useEffect } from "react";

function CalendarModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col"
        style={{ height: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-primary to-secondary px-7 py-5 flex items-center justify-between flex-shrink-0 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-calendar-check-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white font-semibold text-base leading-tight">
                Book Your Free Discovery Call
              </p>
              <p className="text-white/75 text-xs">
                20 minutes · No commitment · No pressure
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
            aria-label="Close modal"
          >
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        {/* Calendar iframe — scrollable container */}
        <div className="flex-1 overflow-y-auto p-4">
          <iframe
            src="https://links.dino-nuggets.com/widget/booking/jYUOBrtBoczrAEOTlhKv"
            style={{ width: "100%", height: "900px", border: "none" }}
            scrolling="no"
            id="jYUOBrtBoczrAEOTlhKv_modal"
            title="Book a Discovery Call"
          />
        </div>
      </div>
    </div>
  );
}

export function CallToAction() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <section id="cta" className="py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          {/* Badge */}
          <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-6 tracking-wide uppercase">
            Free Discovery Call
          </span>

          {/* Headline */}
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-secondary mb-5 leading-tight">
            Ready to Automate Your Business?
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto mb-10 leading-relaxed">
            One click. Pick a time that works for you. We’ll show you exactly how to capture more leads and
            book more appointments — no forms, no back-and-forth.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-primary to-secondary text-white font-semibold text-lg px-10 py-4 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line text-xl"></i>
            Book A Free Demo
          </button>

          <p className="mt-4 text-sm text-gray-400">
            No credit card · No commitment · 20 minutes
          </p>

          {/* Trust row */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-8">
            {[
              { icon: "ri-shield-check-line", label: "No Pressure Sales" },
              { icon: "ri-time-line", label: "20-Min Call" },
              { icon: "ri-rocket-line", label: "Same-Day Start" },
              { icon: "ri-star-fill", label: "Committed to Client Success" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-gray-500 text-sm font-medium">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${icon} text-primary text-lg`}></i>
                </div>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Social proof */}
          <div className="mt-10 inline-flex items-center gap-4 bg-white rounded-2xl shadow-md px-6 py-4">
            <div className="flex -space-x-2">
              {[
                "/readdy-assets/av101.jpg",
                "/readdy-assets/av2.jpg",
                "/readdy-assets/av3.jpg",
              ].map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt="Customer"
                  className="w-9 h-9 rounded-full border-2 border-white object-cover object-top"
                />
              ))}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-0.5 mb-0.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-xs"></i>
                ))}
              </div>
              <p className="text-xs text-gray-500">
                <strong>Delivering Excellence,</strong> Every Project
              </p>
            </div>
          </div>
        </div>
      </section>

      {modalOpen && <CalendarModal onClose={() => setModalOpen(false)} />}
    </>
  );
}